import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Student, SortField, SortDirection, PaginationMeta } from './types';
import {
    fetchStudents as apiFetchStudents,
    fetchClasses,
    fetchSections,
    deleteStudent as apiDeleteStudent,
    mapStudentToUi,
} from '../../services/studentApi';
import type { ClassOption, SectionOption, StudentApiRecord } from '../../services/studentApi';
import type { ApiError } from '../../services/api';
import StudentDetail from './StudentDetail';
import AddStudent from './AddStudent';
import EditStudent from './EditStudent';
import StudentFeeInvoices from './StudentFeeInvoices';
import RupeeIcon from '../icons/RupeeIcon';
import type { InvoiceApiRecord } from '../../services/invoiceApi';
import { printStudentList, type StudentListRow } from '../../utils/printStudentList';
import { printNameSlips } from '../../utils/printNameSlips';
import { useCurrentSchoolName } from '../../hooks/useCurrentSchoolName';
import { fetchTransportAllocations } from '../../services/transportAllocationApi';
import './StudentListing.css';
import { useTranslation, Trans } from 'react-i18next';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

/* The print job covers every student matching the active filters, not just the
   visible page, so it pulls the result set in large chunks. */
const PRINT_FETCH_SIZE = 100;

/** Which print job is in flight — both share one fetch, so only one runs at a time. */
type PrintJob = 'list' | 'slips';

/* Approximate height of the row action menu — used to flip it above the
   trigger when it would otherwise overflow the viewport. */
const ACTION_MENU_HEIGHT = 88;

export default function StudentListing() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const schoolName = useCurrentSchoolName();
    /* ---- State ---- */
    const [students, setStudents] = useState<Student[]>([]);
    const [meta, setMeta] = useState<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 50, total: 0 });
    const [loading, setLoading] = useState(true);
    const [printing, setPrinting] = useState<PrintJob | null>(null);
    const [apiError, setApiError] = useState<string | null>(null);

    /* ---- Draft filter state (not yet applied) ---- */
    const [nameInput, setNameInput] = useState('');
    const [classInput, setClassInput] = useState('');
    const [sectionInput, setSectionInput] = useState('');

    /* ---- Applied filter state (triggers fetch) ---- */
    const [appliedName, setAppliedName] = useState('');
    const [appliedClassId, setAppliedClassId] = useState('');
    const [appliedSectionId, setAppliedSectionId] = useState('');

    const [sortField, setSortField] = useState<SortField>('student_name');
    const [sortDir, setSortDir] = useState<SortDirection>('asc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [actionMenu, setActionMenu] = useState<{ id: string; top: number; left: number } | null>(null);
    const [routeByStudentId, setRouteByStudentId] = useState<Map<string, string>>(new Map());
    const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
    const [availableSections, setAvailableSections] = useState<SectionOption[]>([]);
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editStudentId, setEditStudentId] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<Student | null>(null);
    const [feeStudent, setFeeStudent] = useState<{ id: string; name: string } | null>(null);

    /* ---- Open the Collection Desk pre-loaded with an invoice ---- */
    const handleCollectInvoice = (invoice: InvoiceApiRecord) => {
        setFeeStudent(null);
        navigate('/collection', { state: { invoice } });
    };

    /* ---- Open the Collection Desk on the student, not an invoice: the month grid, where
       months with no invoice yet can be paid for too. ---- */
    const handleCollectMonths = (student: { id: string; name: string }) => {
        setFeeStudent(null);
        navigate('/collection', { state: { student: { id: student.id, name: student.name } } });
    };

    /* ---- Load classes on mount ---- */
    useEffect(() => {
        fetchClasses()
            .then((classes) => setAvailableClasses(classes))
            .catch(() => { /* silently fall back to empty */ });
    }, []);

    /* ---- Load transport allocations on mount — the students list endpoint
       doesn't carry route info, so it's joined client-side by student id. ---- */
    useEffect(() => {
        fetchTransportAllocations({ perPage: 500 })
            .then((allocations) => {
                const map = new Map<string, string>();
                for (const a of allocations) {
                    if (!a.is_active) continue;
                    const title = a.transport_route?.title ?? a.route_stop?.route.title;
                    if (title) map.set(a.student_id, title);
                }
                setRouteByStudentId(map);
            })
            .catch(() => { /* silently fall back to empty */ });
    }, []);

    /* ---- Cascade: reload sections when class selection changes ---- */
    useEffect(() => {
        if (classInput) {
            fetchSections(classInput)
                .then((sections) => setAvailableSections(sections))
                .catch(() => setAvailableSections([]));
        } else {
            setAvailableSections([]);
        }
        setSectionInput('');
    }, [classInput]);

    /* ---- Data Fetching — only triggered by applied filters or page/size ---- */
    const loadStudents = useCallback(async () => {
        setLoading(true);
        setApiError(null);
        try {
            const res = await apiFetchStudents({
                page,
                perPage: pageSize,
                name: appliedName || undefined,
                classId: appliedClassId || undefined,
                sectionId: appliedSectionId || undefined,
            });
            setStudents(res.data.map((s: StudentApiRecord) => mapStudentToUi(s) as Student));
            setMeta(res.meta);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setApiError(apiErr.message || t('students.failedToLoad'));
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, appliedName, appliedClassId, appliedSectionId]);

    useEffect(() => { loadStudents(); }, [loadStudents]);

    /* Reset to page 1 when applied filters change */
    useEffect(() => { setPage(1); }, [appliedName, appliedClassId, appliedSectionId, pageSize]);

    /* ---- Search button handler ---- */
    const handleSearch = () => {
        setAppliedName(nameInput);
        setAppliedClassId(classInput);
        setAppliedSectionId(sectionInput);
    };

    /* ---- Clear all filters ---- */
    const handleClear = () => {
        setNameInput('');
        setClassInput('');
        setSectionInput('');
        setAppliedName('');
        setAppliedClassId('');
        setAppliedSectionId('');
    };

    const hasActiveFilters = appliedName || appliedClassId || appliedSectionId;

    /* ---- Displayed row range (the API meta has no `from`, so derive it) ---- */
    const rowsPerPage = meta.per_page || pageSize;
    const rangeFrom = meta.total === 0 ? 0 : (meta.current_page - 1) * rowsPerPage + 1;
    const rangeTo = Math.min(rangeFrom + rowsPerPage - 1, meta.total);

    /* ---- Print ---- */

    /** Every student matching the applied filters, not just the visible page. */
    const fetchAllForPrint = async (): Promise<StudentListRow[]> => {
        /* Walk every page of the current filter — `last_page` is re-read each time so
           a server-side cap on per_page can't truncate the list. */
        const rows: StudentListRow[] = [];
        let currentPage = 1;
        let lastPage = 1;
        do {
            const res = await apiFetchStudents({
                page: currentPage,
                perPage: PRINT_FETCH_SIZE,
                name: appliedName || undefined,
                classId: appliedClassId || undefined,
                sectionId: appliedSectionId || undefined,
            });
            for (const record of res.data) {
                const s = mapStudentToUi(record);
                rows.push({
                    studentName: s.student_name,
                    fatherName: s.father_name,
                    motherName: s.mother_name,
                    classSection: [s.class_name, s.section_name].filter(Boolean).join(' – ') || null,
                    rollNumber: s.roll_number,
                    dateOfBirth: s.date_of_birth,
                    aadhaarNumber: s.aadhaar_number,
                    fatherContact: s.father_contact,
                    address: s.address,
                    transportRoute: routeByStudentId.get(s.id) ?? null,
                });
            }
            lastPage = res.meta.last_page || 1;
            currentPage += 1;
        } while (currentPage <= lastPage);
        return rows;
    };

    /** Both sheets are per-class documents, so they always print against an applied
     *  class filter — never the whole school. */
    const runPrint = async (job: PrintJob, render: (rows: StudentListRow[]) => void) => {
        if (!appliedClassId) return;
        setPrinting(job);
        setApiError(null);
        try {
            const rows = await fetchAllForPrint();
            if (rows.length === 0) {
                setApiError(t('students.nothingToPrint'));
                return;
            }
            render(rows);
        } catch (err: unknown) {
            const apiErr = err as ApiError;
            setApiError(apiErr.message || t('students.printFailed'));
        } finally {
            setPrinting(null);
        }
    };

    const appliedClassName = () => availableClasses.find((c) => c.id === appliedClassId)?.name;
    const appliedSectionName = () => availableSections.find((s) => s.id === appliedSectionId)?.name;

    const handlePrintList = () => runPrint('list', (rows) => printStudentList({
        schoolName: schoolName ?? undefined,
        className: appliedClassName(),
        sectionName: appliedSectionName(),
        searchTerm: appliedName || undefined,
        rows,
    }));

    const handlePrintNameSlips = () => runPrint('slips', (rows) => printNameSlips({
        schoolName: schoolName ?? undefined,
        className: appliedClassName(),
        sectionName: appliedSectionName(),
        rows,
    }));

    /* ---- Sorting ---- */
    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const sortIndicator = (field: SortField) => {
        if (sortField !== field) return <span className="students__sort-icon students__sort-icon--inactive">⇅</span>;
        return <span className="students__sort-icon">{sortDir === 'asc' ? '▲' : '▼'}</span>;
    };

    /* ---- Row action menu (⋯) ---- */
    const openActionMenu = (e: React.MouseEvent<HTMLButtonElement>, id: string) => {
        if (actionMenu?.id === id) {
            setActionMenu(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        // Flip the menu above the trigger when it would overflow the viewport
        const openUp = rect.bottom + ACTION_MENU_HEIGHT + 8 > window.innerHeight;
        setActionMenu({
            id,
            top: openUp ? rect.top - ACTION_MENU_HEIGHT - 4 : rect.bottom + 4,
            left: rect.right,
        });
    };

    /* Close the action menu on Escape */
    useEffect(() => {
        if (!actionMenu) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActionMenu(null);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [actionMenu]);

    /* ---- Pagination helpers ---- */
    const pageNumbers = (): number[] => {
        const pages: number[] = [];
        const start = Math.max(1, meta.current_page - 2);
        const end = Math.min(meta.last_page, meta.current_page + 2);
        for (let i = start; i <= end; i++) pages.push(i);
        return pages;
    };

    /* ---- Null-safe renderers ---- */
    const renderName = (s: Student) => (
        <button className="students__name-link" title={t('students.viewProfile', { name: s.student_name })} onClick={() => setSelectedStudentId(s.id)}>
            {s.student_name}
        </button>
    );

    const renderClass = (s: Student) => {
        if (!s.class_name) return <span className="students__muted">{t('common.unassigned')}</span>;
        return s.class_name;
    };

    const renderTransportRoute = (s: Student) => {
        const route = routeByStudentId.get(s.id);
        if (!route) return <span className="students__muted">{t('common.unassigned')}</span>;
        return route;
    };

    const renderTextOrDash = (value: string | null) => value ?? <span className="students__muted">—</span>;

    const renderContact = (phone: string | null, email: string | null) => {
        if (!phone && !email) return <span className="students__muted">—</span>;
        return (
            <span className="students__contact-cell">
                {phone && <a href={`tel:${phone}`} className="students__contact-link">{phone}</a>}
                {phone && email && <span className="students__contact-sep"> · </span>}
                {email && <a href={`mailto:${email}`} className="students__contact-link students__contact-link--email">{email}</a>}
            </span>
        );
    };

    /* ================================================================= */
    /*  Render                                                            */
    /* ================================================================= */
    return (
        <div className="students">
            {/* ---- API Error Banner ---- */}
            {apiError && (
                <div className="students__error-banner" role="alert">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                    <span>{apiError}</span>
                    <button className="students__error-dismiss" onClick={() => setApiError(null)}>×</button>
                </div>
            )}

            {/* ---- Toolbar ---- */}
            <div className="students__toolbar">
                <div className="students__toolbar-left">
                    {/* Name search */}
                    <div className="students__search-wrapper">
                        <svg className="students__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            className="students__search"
                            type="text"
                            placeholder={t('students.searchPlaceholder')}
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        {nameInput && (
                            <button className="students__search-clear" onClick={() => setNameInput('')} aria-label="Clear search">×</button>
                        )}
                    </div>

                    {/* Class filter */}
                    <select
                        className="students__filter"
                        value={classInput}
                        onChange={(e) => setClassInput(e.target.value)}
                    >
                        <option value="">{t('students.allClasses')}</option>
                        {availableClasses.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>

                    {/* Section filter — enabled only when a class is selected */}
                    <select
                        className="students__filter"
                        value={sectionInput}
                        onChange={(e) => setSectionInput(e.target.value)}
                        disabled={!classInput}
                    >
                        <option value="">{t('students.allSections')}</option>
                        {availableSections.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>

                    {/* Search button */}
                    <button className="students__search-btn" onClick={handleSearch}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        {t('common.search')}
                    </button>

                    {/* Clear button — only shown when filters are active */}
                    {hasActiveFilters && (
                        <button className="students__clear-btn" onClick={handleClear} title={t('students.clearFilters')}>
                            {t('common.clear')}
                        </button>
                    )}
                </div>

                <div className="students__toolbar-right">
                    <span className="students__record-count">{t('students.studentCount', { count: meta.total })}</span>
                    <button
                        className="students__print-btn"
                        onClick={handlePrintList}
                        disabled={printing !== null || loading || !appliedClassId || meta.total === 0}
                        title={appliedClassId ? t('students.printListHint') : t('students.selectClassToPrint')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                        </svg>
                        {printing === 'list' ? t('students.preparingPrint') : t('students.printList')}
                    </button>
                    <button
                        className="students__print-btn"
                        onClick={handlePrintNameSlips}
                        disabled={printing !== null || loading || !appliedClassId || meta.total === 0}
                        title={appliedClassId ? t('students.printNameSlipsHint') : t('students.selectClassToPrint')}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="7" rx="1" />
                            <rect x="3" y="14" width="18" height="7" rx="1" />
                            <line x1="7" y1="7.5" x2="12" y2="7.5" />
                            <line x1="7" y1="17.5" x2="12" y2="17.5" />
                        </svg>
                        {printing === 'slips' ? t('students.preparingPrint') : t('students.printNameSlips')}
                    </button>
                    <button className="students__add-btn" onClick={() => setShowAddForm(true)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                        </svg>
                        {t('students.addStudent')}
                    </button>
                </div>
            </div>

            {/* ---- Data Grid ---- */}
            <div className="students__table-wrap">
                <table className="students__table">
                    <thead>
                        <tr>
                            <th className="students__th students__th--name" onClick={() => handleSort('student_name')}>
                                {t('students.studentName')} {sortIndicator('student_name')}
                            </th>
                            <th className="students__th students__th--father">{t('students.father')}</th>
                            <th className="students__th students__th--mother">{t('students.mother')}</th>
                            <th className="students__th students__th--class">{t('students.class')}</th>
                            <th className="students__th students__th--address">{t('students.address')}</th>
                            <th className="students__th students__th--route">{t('students.transportRoute')}</th>
                            <th className="students__th students__th--contact">{t('students.parentContact')}</th>
                            <th className="students__th students__th--actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={8} className="students__loading-cell">
                                    <div className="students__loader">
                                        <span className="students__loader-dot" />
                                        <span className="students__loader-dot" />
                                        <span className="students__loader-dot" />
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && students.length === 0 && (
                            <tr>
                                <td colSpan={8} className="students__empty-cell">
                                    <div className="students__empty">
                                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.35">
                                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                        </svg>
                                        <p>{t('students.noStudentsFound')}</p>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {!loading && students.map((s) => (
                            <tr key={s.id} className="students__row">
                                <td className="students__td students__td--name">{renderName(s)}</td>
                                <td className="students__td students__td--father">{renderTextOrDash(s.father_name ?? null)}</td>
                                <td className="students__td students__td--mother">{renderTextOrDash(s.mother_name ?? null)}</td>
                                <td className="students__td students__td--class">{renderClass(s)}</td>
                                <td className="students__td students__td--address">{renderTextOrDash(s.address ?? null)}</td>
                                <td className="students__td students__td--route">{renderTransportRoute(s)}</td>
                                <td className="students__td students__td--contact">{renderContact(s.parent_phone, s.parent_email)}</td>
                                <td className="students__td students__td--actions">
                                    <div className="students__row-actions">
                                        <button
                                            className="students__fee-btn"
                                            onClick={() => setFeeStudent({ id: s.id, name: s.student_name })}
                                            title={t('students.viewFees', 'View fees for {{name}}', { name: s.student_name })}
                                        >
                                            <RupeeIcon size={14} />
                                            {t('students.fee', 'Fee')}
                                        </button>
                                        <button
                                            className={`students__expand-btn ${actionMenu?.id === s.id ? 'students__expand-btn--open' : ''}`}
                                            onClick={(e) => openActionMenu(e, s.id)}
                                            title={t('students.rowActions')}
                                            aria-label={t('students.rowActions')}
                                            aria-haspopup="menu"
                                            aria-expanded={actionMenu?.id === s.id}
                                        >⋯</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ---- Pagination ---- */}
            {!loading && meta.total > 0 && (
                <div className="students__pagination">
                    <div className="students__pagination-left">
                        <label className="students__page-size-label">
                            {t('common.rowsPerPage')}
                            <select className="students__page-size" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </label>
                        <span className="students__page-info">
                            {t('common.showing', { from: rangeFrom, to: rangeTo, total: meta.total })}
                        </span>
                    </div>

                    <div className="students__pagination-right">
                        <button className="students__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage(1)} title={t('common.firstPage')}>«</button>
                        <button className="students__page-btn" disabled={meta.current_page <= 1} onClick={() => setPage((p) => p - 1)} title={t('common.previousPage')}>‹</button>
                        {pageNumbers().map((n) => (
                            <button
                                key={n}
                                className={`students__page-btn ${n === meta.current_page ? 'students__page-btn--active' : ''}`}
                                onClick={() => setPage(n)}
                            >{n}</button>
                        ))}
                        <button className="students__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage((p) => p + 1)} title={t('common.nextPage')}>›</button>
                        <button className="students__page-btn" disabled={meta.current_page >= meta.last_page} onClick={() => setPage(meta.last_page)} title={t('common.lastPage')}>»</button>
                    </div>
                </div>
            )}

            {/* Student Detail Drawer */}
            {selectedStudentId && (
                <StudentDetail
                    studentId={selectedStudentId}
                    onClose={() => setSelectedStudentId(null)}
                    onEdit={(id) => {
                        setSelectedStudentId(null);
                        setEditStudentId(id);
                    }}
                />
            )}

            {/* Add Student Drawer */}
            {showAddForm && (
                <AddStudent
                    onClose={() => setShowAddForm(false)}
                    onSave={() => {
                        setShowAddForm(false);
                        loadStudents();
                    }}
                />
            )}

            {/* Student Fee Invoices Modal */}
            {feeStudent && (
                <StudentFeeInvoices
                    studentId={feeStudent.id}
                    studentName={feeStudent.name}
                    onClose={() => setFeeStudent(null)}
                    onCollect={handleCollectInvoice}
                    onCollectMonths={() => handleCollectMonths(feeStudent)}
                />
            )}

            {/* Edit Student Drawer */}
            {editStudentId && (
                <EditStudent
                    studentId={editStudentId}
                    onClose={() => setEditStudentId(null)}
                    onSave={() => {
                        setEditStudentId(null);
                        loadStudents();
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirm && (
                <>
                    <div className="students__modal-backdrop" onClick={() => setDeleteConfirm(null)} />
                    <div className="students__modal" role="dialog">
                        <h3 className="students__modal-title">{t('students.removeStudent')}</h3>
                        <p className="students__modal-text">
                            <Trans
                                i18nKey="students.confirmRemoveStudent"
                                values={{ name: deleteConfirm.student_name }}
                                components={[<span />, <strong />]}
                            />
                        </p>
                        <div className="students__modal-actions">
                            <button className="students__modal-btn students__modal-btn--cancel" onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</button>
                            <button
                                className="students__modal-btn students__modal-btn--delete"
                                onClick={async () => {
                                    try {
                                        await apiDeleteStudent(deleteConfirm.id);
                                        setDeleteConfirm(null);
                                        loadStudents();
                                    } catch (err: unknown) {
                                        const apiErr = err as ApiError;
                                        setApiError(apiErr.message || t('students.failedToDelete'));
                                        setDeleteConfirm(null);
                                    }
                                }}
                            >{t('common.delete')}</button>
                        </div>
                    </div>
                </>
            )}

            {/* Row Action Menu — Edit / Delete */}
            {actionMenu && (
                <>
                    <div className="students__action-overlay" onClick={() => setActionMenu(null)} />
                    <div
                        className="students__action-menu"
                        style={{ top: actionMenu.top, left: actionMenu.left }}
                        role="menu"
                    >
                        <button
                            className="students__action-item"
                            role="menuitem"
                            onClick={() => {
                                setEditStudentId(actionMenu.id);
                                setActionMenu(null);
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z" />
                            </svg>
                            {t('students.editStudent')}
                        </button>
                        <button
                            className="students__action-item students__action-item--danger"
                            role="menuitem"
                            onClick={() => {
                                const student = students.find((st) => st.id === actionMenu.id);
                                if (student) setDeleteConfirm(student);
                                setActionMenu(null);
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            {t('students.deleteStudent')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
